import type { ReploErrorConfig } from "schemas/reploError";
import * as z from "zod";

import asyncRetry from "async-retry";
import { getErrorMessage } from "replo-utils/lib/errors";
import { parseJsonOrNull } from "replo-utils/lib/json";
import { ReploError } from "schemas/reploError";
import { isRetryableNetworkError } from "worker-utils/fetch-retry";

export class HttpClientError extends ReploError {
  /**
   * Names the client that made the request (e.g. "Nango"), so a catch site
   * fronting more than one upstream can tell which one failed.
   */
  readonly clientIdentifier: string;

  constructor({
    clientIdentifier,
    ...config
  }: ReploErrorConfig & { clientIdentifier: string }) {
    // Also in additionalData so Sentry and log reporters, which only read
    // ReploError config, still show which upstream failed.
    super({
      ...config,
      additionalData: { ...config.additionalData, clientIdentifier },
    });
    this.clientIdentifier = clientIdentifier;
  }
}

export class HttpStatusError extends HttpClientError {
  statusCode: number;
  responseBody: string | undefined;
  responseHeaders: Record<string, string>;

  constructor({
    message,
    statusCode,
    responseBody,
    responseHeaders,
    clientIdentifier,
  }: {
    message: string;
    statusCode: number;
    responseBody?: string;
    responseHeaders: Record<string, string>;
    clientIdentifier: string;
  }) {
    super({
      message,
      clientIdentifier,
      additionalData: { statusCode, responseBody },
    });
    this.statusCode = statusCode;
    this.responseBody = responseBody;
    this.responseHeaders = responseHeaders;
  }
}

class HttpNetworkError extends HttpClientError {}
class RetriableHttpError extends HttpNetworkError {}

/** Distinguish the client's elapsed deadline from an upstream network failure. */
export class HttpTimeoutError extends HttpClientError {}

// The status is retryable in principle, but the server named a wait longer than
// this client could ever sleep, so retrying can only add load and still fail.
class HttpRetryBudgetExceededError extends HttpClientError {}

export class HttpParseError extends HttpClientError {}

/** Retry failures store status in additionalData rather than HttpStatusError. */
export function getHttpErrorStatusCode(error: unknown): number | null {
  if (error instanceof HttpStatusError) {
    return error.statusCode;
  }
  if (!(error instanceof ReploError)) {
    return null;
  }
  const statusCode = error.config.additionalData?.statusCode;
  return typeof statusCode === "number" ? statusCode : null;
}

/** Durable retry queues may retry exhausted throttles and outages; stable 4xx failures remain permanent. */
export function isRetryableLaterHttpError(error: unknown): boolean {
  if (
    error instanceof RetriableHttpError ||
    error instanceof HttpRetryBudgetExceededError
  ) {
    return true;
  }
  if (error instanceof HttpStatusError) {
    return false;
  }
  const statusCode = getHttpErrorStatusCode(error);
  if (statusCode === 429 || (statusCode !== null && statusCode >= 500)) {
    return true;
  }
  return (
    error instanceof ReploError &&
    error.message.startsWith("Retryable network error")
  );
}

// Treat waits beyond a day as invalid, including epoch timestamps mistaken for delta-seconds.
const MAX_PLAUSIBLE_RETRY_AFTER_MS = 24 * 60 * 60 * 1000;

// Retry-After accepts delta-seconds or HTTP dates; unusable values fall back to the normal retry schedule.
function parseRetryAfterMs(headerValue: string | null): number | null {
  if (!headerValue) {
    return null;
  }
  const seconds = Number(headerValue);
  const deltaMs = Number.isFinite(seconds)
    ? Math.max(seconds, 0) * 1000
    : Math.max(Date.parse(headerValue) - Date.now(), 0);
  if (Number.isNaN(deltaMs) || deltaMs > MAX_PLAUSIBLE_RETRY_AFTER_MS) {
    return null;
  }
  return deltaMs;
}

type Fetcher = (input: string, init: RequestInit) => Promise<Response>;

// NOTE (Kevin O'Connell, 2026-07-15): Mirrors got's default retry.statusCodes
// (Node HTTP outside Workers) and adds Cloudflare 520, which got omits.
const DEFAULT_RETRYABLE_STATUS_CODES = [
  408, 413, 429, 500, 502, 503, 504, 520, 521, 522, 524,
];

// Deeper chains add noise rather than diagnosis, and this bounds a cyclic `cause`.
const MAX_ERROR_CHAIN_DEPTH = 5;

// `Error.cause` is ES2022 and this package targets an older lib, so `in` narrows it.
function getErrorCause(error: unknown): unknown {
  // undici wraps a multi-address failure in an AggregateError with no message.
  if (error instanceof AggregateError && error.errors.length > 0) {
    return error.errors[0];
  }
  return error instanceof Error && "cause" in error ? error.cause : undefined;
}

// NOTE (Ryan, 2026-08-20): undici reports every network failure as a bare "fetch
// failed" and hides the real one (ENOTFOUND, ECONNRESET) in the cause chain.
function getErrorChainMessage(error: unknown): string {
  const messages: string[] = [];
  let current: unknown = error;
  for (let depth = 0; depth < MAX_ERROR_CHAIN_DEPTH; depth++) {
    const message = getErrorMessage(current);
    if (message) {
      messages.push(message);
    }
    const cause = getErrorCause(current);
    if (cause === undefined || cause === null) {
      break;
    }
    current = cause;
  }
  return messages.join(": ");
}

// Workers cannot use got, so this client provides retries around native fetch.
export class AgentHTTPClient {
  private readonly baseUrl: string;
  private readonly headers: Record<string, string>;
  private readonly retries: number;
  private readonly discardErrorBody: boolean;
  private readonly timeoutMs: number | undefined;
  private readonly retryableStatusCodes: number[];
  private readonly minTimeout: number;
  private readonly maxRetrySleepMs: number;
  private readonly onRetry:
    | ((error: Error, attempt: number) => void)
    | undefined;
  private readonly fetch: Fetcher;
  private readonly identifier: string;

  constructor(opts: {
    baseUrl: string;
    headers?: Record<string, string>;
    /** Names this client's upstream (e.g. "Nango") on every error it throws. */
    identifier: string;
    retry?: {
      retries?: number;
      retryableStatusCodes?: number[];
      minTimeout?: number;
    };
    discardErrorBody?: boolean;
    /** Whole-call deadline: spans every retry attempt and backoff sleep. */
    timeoutMs?: number;
    onRetry?: (error: Error, attempt: number) => void;
    fetch?: Fetcher;
  }) {
    this.baseUrl = opts.baseUrl;
    this.headers = opts.headers ?? {};
    this.identifier = opts.identifier;
    // Workers' fetch rejects the client as its receiver; a lexical closure preserves the native receiver.
    this.fetch = opts.fetch ?? ((url, init) => fetch(url, init));
    this.retries = opts.retry?.retries ?? 2;
    this.discardErrorBody = opts.discardErrorBody ?? false;
    this.timeoutMs = opts.timeoutMs;
    this.retryableStatusCodes =
      opts.retry?.retryableStatusCodes ?? DEFAULT_RETRYABLE_STATUS_CODES;
    this.minTimeout = opts.retry?.minTimeout ?? 500;
    // async-retry doubles the wait each attempt and jitters each one by up to
    // 2x, so this is the longest it could sleep across every retry.
    this.maxRetrySleepMs = 2 * this.minTimeout * (2 ** this.retries - 1);
    this.onRetry = opts.onRetry;
  }

  async post<SchemaType extends z.ZodType>({
    path,
    body,
    schema,
    headers,
  }: {
    path: string;
    body: Record<string, unknown> | unknown[];
    schema: SchemaType;
    /** Per-request headers (e.g. an Idempotency-Key); override constructor headers on collision. */
    headers?: Record<string, string>;
  }): Promise<z.infer<SchemaType>> {
    return this.request({
      path,
      schema,
      init: {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...this.headers,
          ...headers,
        },
        body: JSON.stringify(body),
      },
    });
  }

  async put<SchemaType extends z.ZodType>({
    path,
    body,
    schema,
  }: {
    path: string;
    body: Record<string, unknown> | unknown[];
    schema: SchemaType;
  }): Promise<z.infer<SchemaType>> {
    return this.request({
      path,
      schema,
      init: {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...this.headers },
        body: JSON.stringify(body),
      },
    });
  }

  async patch<SchemaType extends z.ZodType>({
    path,
    body,
    schema,
  }: {
    path: string;
    body: Record<string, unknown> | unknown[];
    schema: SchemaType;
  }): Promise<z.infer<SchemaType>> {
    return this.request({
      path,
      schema,
      init: {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...this.headers },
        body: JSON.stringify(body),
      },
    });
  }

  // Providers such as Stripe require nested form keys and per-request account headers.
  async postForm<SchemaType extends z.ZodType>({
    path,
    form,
    schema,
    headers,
  }: {
    path: string;
    form: URLSearchParams;
    schema: SchemaType;
    headers?: Record<string, string>;
  }): Promise<z.infer<SchemaType>> {
    return this.request({
      path,
      schema,
      init: {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          ...this.headers,
          ...headers,
        },
        body: form.toString(),
      },
    });
  }

  async putBlob({
    path,
    body,
    contentType,
  }: {
    path: string;
    body: Blob;
    contentType: string;
  }) {
    const url = path ? `${this.baseUrl}/${path}` : this.baseUrl;
    const response = await this.fetchWithRetry(url, {
      method: "PUT",
      headers: { ...this.headers, "Content-Type": contentType },
      body,
    });
    await response.body?.cancel();
  }

  async postMultipartRaw({
    path,
    form,
    headers,
  }: {
    path: string;
    form: FormData;
    headers?: Record<string, string>;
  }): Promise<Response> {
    const url = path ? `${this.baseUrl}/${path}` : this.baseUrl;
    return this.fetchWithRetry(url, {
      method: "POST",
      headers: { ...this.headers, ...headers },
      body: form,
    });
  }

  // `headers` overlays per-request values the base client can't carry, like
  // Stripe's per-call `Stripe-Account` (same contract as postForm).
  async get<SchemaType extends z.ZodType>({
    path,
    schema,
    headers,
  }: {
    path: string;
    schema: SchemaType;
    headers?: Record<string, string>;
  }): Promise<z.infer<SchemaType>> {
    return this.request({
      path,
      schema,
      init: { method: "GET", headers: { ...this.headers, ...headers } },
    });
  }

  async getText({ path }: { path: string }): Promise<string> {
    const url = path ? `${this.baseUrl}/${path}` : this.baseUrl;
    const response = await this.fetchWithRetry(url, {
      method: "GET",
      headers: this.headers,
    });
    return response.text();
  }

  async getBuffer({ path }: { path: string }): Promise<Uint8Array> {
    const url = path ? `${this.baseUrl}/${path}` : this.baseUrl;
    const response = await this.fetchWithRetry(url, {
      method: "GET",
      headers: this.headers,
    });
    return new Uint8Array(await response.arrayBuffer());
  }

  // TODO (Kevin, 2026-07-29, REPL-30025): Delete this alias; move nango-worker's downloadUrl to requestRaw, which supersedes it.
  async getRaw({
    path,
    headers,
  }: {
    path: string;
    headers?: Record<string, string>;
  }): Promise<Response> {
    return this.requestRaw({ path, headers });
  }

  // Raw 2xx Response, body unconsumed, for streaming reads or a chosen method/JSON body; non-2xx throws HttpStatusError.
  async requestRaw({
    path,
    method = "GET",
    headers,
    body,
    signal,
  }: {
    path: string;
    signal?: AbortSignal;
    method?: "GET" | "POST";
    headers?: Record<string, string>;
    body?: Record<string, unknown> | unknown[];
  }): Promise<Response> {
    const url = path ? `${this.baseUrl}/${path}` : this.baseUrl;
    return this.fetchWithRetry(url, {
      method,
      signal,
      headers: {
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...this.headers,
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  }

  async delete<SchemaType extends z.ZodType>({
    path,
    schema,
  }: {
    path: string;
    schema: SchemaType;
  }): Promise<z.infer<SchemaType>> {
    return this.request({
      path,
      schema,
      init: { method: "DELETE", headers: this.headers },
    });
  }

  private async request<SchemaType extends z.ZodType>({
    path,
    schema,
    init,
  }: {
    path: string;
    schema: SchemaType;
    init: RequestInit;
  }): Promise<z.infer<SchemaType>> {
    const url = path ? `${this.baseUrl}/${path}` : this.baseUrl;
    let responseBody: string | undefined;
    try {
      const response = await this.fetchWithRetry(url, init);
      responseBody = await response.text();
      const json = parseJsonOrNull(responseBody);
      return schema.parse(json);
    } catch (error) {
      if (error instanceof HttpStatusError) {
        throw error;
      }
      this.handleDefaultError({ path, error, responseBody });
    }
  }

  private handleDefaultError({
    path,
    error,
    responseBody,
  }: {
    path: string;
    error: unknown;
    responseBody?: string;
  }): never {
    if (error instanceof z.ZodError) {
      throw new HttpParseError({
        message: "Error parsing response",
        clientIdentifier: this.identifier,
        additionalData: {
          path,
          issues: error.issues,
          baseUrl: this.baseUrl,
          responseBody,
        },
        cause: error,
      });
    }
    throw error;
  }

  private async fetchWithRetry(url: string, init: RequestInit) {
    if (this.timeoutMs === undefined) {
      return await this.attemptWithRetry(url, init, undefined);
    }
    const timeoutMs = this.timeoutMs;
    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    const request = {
      ...init,
      signal: AbortSignal.any([
        ...(init.signal ? [init.signal] : []),
        timeoutSignal,
      ]),
    };
    const attempt = this.attemptWithRetry(url, request, timeoutSignal);
    // Race async-retry because its backoff sleeps cannot observe an abort.
    const controller = new AbortController();
    const deadline = new Promise<never>((_, reject) => {
      timeoutSignal.addEventListener(
        "abort",
        () => {
          reject(
            new HttpTimeoutError({
              message: `Request to ${url} exceeded ${timeoutMs}ms`,
              clientIdentifier: this.identifier,
              additionalData: {
                url,
                timeoutMs,
              },
            }),
          );
        },
        { once: true, signal: controller.signal },
      );
    });
    // The abandoned attempt's fetch is already aborted by the shared signal, but
    // its rejection still needs an owner or losing the race becomes unhandled.
    attempt.catch(() => {});
    try {
      return await Promise.race([attempt, deadline]);
    } finally {
      controller.abort();
    }
  }

  private async attemptWithRetry(
    url: string,
    request: RequestInit,
    timeoutSignal: AbortSignal | undefined,
  ) {
    return asyncRetry(
      async () => {
        let response: Response;
        try {
          request.signal?.throwIfAborted();
          response = await this.fetch(url, request);
        } catch (error) {
          // Checked before the generic abort branch so the deadline always
          // surfaces as the same type, whichever path notices it first.
          if (timeoutSignal?.aborted) {
            throw Object.assign(
              new HttpTimeoutError({
                message: `Request to ${url} exceeded ${this.timeoutMs}ms`,
                clientIdentifier: this.identifier,
                additionalData: {
                  url,
                  timeoutMs: this.timeoutMs,
                },
                cause: error,
              }),
              { bail: true },
            );
          }
          if (request.signal?.aborted || !isRetryableNetworkError(error)) {
            throw Object.assign(
              new HttpNetworkError({
                message: `Non-retryable network error from ${url}: ${getErrorChainMessage(error)}`,
                clientIdentifier: this.identifier,
                additionalData: { url },
                cause: error,
              }),
              { bail: true },
            );
          }

          throw new RetriableHttpError({
            message: `Retryable network error from ${url}: ${getErrorChainMessage(error)}`,
            clientIdentifier: this.identifier,
            additionalData: { url },
            cause: error,
          });
        }

        if (!response.ok && this.discardErrorBody) {
          await response.body?.cancel();
        }
        if (this.retryableStatusCodes.includes(response.status)) {
          const retryAfterMs = parseRetryAfterMs(
            response.headers.get("retry-after"),
          );
          if (retryAfterMs !== null && retryAfterMs > this.maxRetrySleepMs) {
            throw Object.assign(
              new HttpRetryBudgetExceededError({
                message: `Status ${response.status} from ${url} asked to retry after ${Math.round(retryAfterMs / 1000)}s`,
                clientIdentifier: this.identifier,
                additionalData: {
                  statusCode: response.status,
                  url,
                  retryAfterMs,
                },
              }),
              { bail: true },
            );
          }

          throw new RetriableHttpError({
            message: `Retryable status ${response.status} from ${url}`,
            clientIdentifier: this.identifier,
            additionalData: {
              statusCode: response.status,
              url,
            },
          });
        }

        if (!response.ok) {
          const responseBody = this.discardErrorBody
            ? undefined
            : await response.text();
          throw Object.assign(
            new HttpStatusError({
              message: `Non-2xx response: ${response.status} from ${url}`,
              statusCode: response.status,
              responseBody,
              responseHeaders: Object.fromEntries(response.headers),
              clientIdentifier: this.identifier,
            }),
            { bail: true },
          );
        }

        return response;
      },
      {
        retries: this.retries,
        minTimeout: this.minTimeout,
        onRetry: this.onRetry,
      },
    );
  }
}
